import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '../../components/ui/Button';
import { Pagination } from '../../components/ui/Pagination';
import { Modal } from '../../components/ui/Modal';
import { DataCard, DataCardActions, DataCardEmpty, DataCardRow } from '../../components/ui/DataCard';
import { TextField } from '../../components/forms/TextField';
import { CurrencyInput } from '../../components/forms/CurrencyInput';
import { cn } from '../../utils/cn';
import { formatPrice } from '../../utils/formatPrice';
import { bookingApi, type BookingEntity, type ServiceEntity } from '../../api/BookingApi';
import { useI18n } from '../../i18n/I18nContext';

/* ------------------------------------------------------------------ */
/*  Inline helpers                                                     */
/* ------------------------------------------------------------------ */

function Badge({
  children,
  tone,
}: {
  children: React.ReactNode;
  tone: 'amber' | 'blue' | 'green' | 'rose' | 'slate';
}) {
  const tones: Record<typeof tone, string> = {
    amber: 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300',
    blue: 'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300',
    green: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300',
    rose: 'bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300',
    slate: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  };
  return (
    <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize', tones[tone])}>
      {children}
    </span>
  );
}

const BOOKING_STATUSES = ['pending', 'confirmed', 'cancelled', 'completed', 'no_show'] as const;
const DEFAULT_ACTIVE_STATUSES: Record<string, boolean> = {
  pending: true,
  confirmed: true,
  cancelled: false,
  completed: true,
  no_show: true,
};
const PAYMENT_STATUSES = ['unpaid', 'paid', 'partial', 'overpaid', 'void'];
const PAYMENT_METHODS = ['cash', 'credit', 'debit', 'ewallet', 'bank_transfer', 'voucher', 'point', 'other'];

const statusTone = (s: string): 'amber' | 'blue' | 'green' | 'rose' | 'slate' => {
  switch (s) {
    case 'pending': return 'amber';
    case 'confirmed': return 'blue';
    case 'completed': return 'green';
    case 'cancelled': return 'rose';
    case 'no_show': return 'slate';
    default: return 'slate';
  }
};

const paymentTone = (s: string): 'amber' | 'blue' | 'green' | 'rose' | 'slate' => {
  switch (s) {
    case 'unpaid': return 'rose';
    case 'paid': return 'green';
    case 'partial': return 'amber';
    case 'overpaid': return 'blue';
    case 'void': return 'slate';
    default: return 'slate';
  }
};

/** Converts snake_case enum values (e.g. "no_show") into camelCase i18n keys (e.g. "noShow"). */
function toCamel(s: string): string {
  return s.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase());
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  const day = String(d.getDate()).padStart(2, '0');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const mon = months[d.getMonth()];
  const year = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${day} ${mon} ${year} ${hh}:${mm}`;
}

function toDatetimeLocal(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function getServiceNames(b: BookingEntity): string {
  if (b.serviceJson && Array.isArray(b.serviceJson) && b.serviceJson.length > 0) {
    return b.serviceJson.map((s) => s.title).join(', ');
  }
  return b.service?.title ?? '—';
}

const selectClass = 'w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm outline-none focus:border-red-400 focus:ring-2 focus:ring-red-200 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100';

/* ------------------------------------------------------------------ */
/*  Detail row                                                         */
/* ------------------------------------------------------------------ */

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className="flex flex-col gap-0.5 py-1.5 text-sm sm:flex-row sm:gap-3">
      <div className="font-medium text-slate-500 sm:w-36 sm:shrink-0 dark:text-slate-400">{label}</div>
      <div className="min-w-0 break-words text-slate-900 dark:text-slate-100">{value}</div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Edit form draft                                                    */
/* ------------------------------------------------------------------ */

type ServiceMapDraft = {
  serviceId: string;
  attendedBy: string[];
  price: string;
  serviceBookingItemId?: string;
};

type BookingDraft = {
  reservationDateTime: string;
  paymentStatus: string;
  paymentMethod: string;
  status: string;
  remark: string;
  attendedAddress: string;
  isExternal: boolean;
  serviceMap: ServiceMapDraft[];
};

function bookingToDraft(b: BookingEntity): BookingDraft {
  let serviceMap: ServiceMapDraft[] = [];

  const jsonByServiceId = new Map(
    (b.serviceJson ?? []).map((s) => [s.serviceId, s]),
  );

  if (b.metadata?.serviceMap && Array.isArray(b.metadata.serviceMap)) {
    serviceMap = b.metadata.serviceMap.map((entry) => {
      const snap = jsonByServiceId.get(entry.serviceId);
      return {
        serviceId: entry.serviceId,
        attendedBy: Array.isArray(entry.attendedBy) ? entry.attendedBy : [],
        price: entry.price != null ? String(entry.price) : snap ? String(snap.price) : '',
        serviceBookingItemId: entry.serviceBookingItemId,
      };
    });
  } else if (b.serviceJson && Array.isArray(b.serviceJson) && b.serviceJson.length > 0) {
    serviceMap = b.serviceJson.map((svc) => ({
      serviceId: svc.serviceId,
      attendedBy: b.attendedByArray || (b.attendedBy ? [b.attendedBy] : []),
      price: String(svc.price),
    }));
  } else if (b.serviceId) {
    serviceMap = [{
      serviceId: b.serviceId,
      attendedBy: b.attendedBy ? [b.attendedBy] : [],
      price: String(b.totalPrice),
    }];
  }

  return {
    reservationDateTime: toDatetimeLocal(b.reservationDateTime),
    paymentStatus: b.paymentStatus || 'unpaid',
    paymentMethod: b.paymentMethod || 'cash',
    status: b.status,
    remark: b.remark || '',
    attendedAddress: b.attendedAddress || '',
    isExternal: b.isExternal || false,
    serviceMap,
  };
}

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

export function BookingsPage() {
  const { t } = useI18n();
  const [bookings, setBookings] = useState<BookingEntity[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [activeStatuses, setActiveStatuses] = useState<Record<string, boolean>>({ ...DEFAULT_ACTIVE_STATUSES });
  const [search, setSearch] = useState('');
  const [limit, setLimit] = useState(10);
  const [skip, setSkip] = useState(0);

  const [selected, setSelected] = useState<BookingEntity | null>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<BookingDraft | null>(null);
  const [saving, setSaving] = useState(false);

  const [allServices, setAllServices] = useState<ServiceEntity[]>([]);
  const [allStaff, setAllStaff] = useState<Array<{ userId: string; name: string }>>([]);
  const [lookupLoaded, setLookupLoaded] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /* ---- Fetch (server-side pagination via v3) ---- */

  const loadBookings = useCallback(async (params: {
    skip: number; limit: number; statuses: Record<string, boolean>; search: string; sortOrder: 'ASC' | 'DESC';
  }) => {
    setLoading(true);
    setError(null);
    try {
      const query: Record<string, string | number> = {
        skip: params.skip,
        limit: params.limit,
        sortOrder: params.sortOrder,
      };
      const on = BOOKING_STATUSES.filter((s) => params.statuses[s]);
      if (on.length > 0 && on.length < BOOKING_STATUSES.length) {
        query.status = on.join(',');
      }
      if (params.search.trim()) query.search = params.search.trim();
      const res = await bookingApi.listBookings(query as any);
      const body = res.data;
      setBookings(body.data);
      setTotal(body.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('booking.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadBookings({ skip, limit, statuses: activeStatuses, search, sortOrder: 'DESC' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function refresh() {
    loadBookings({ skip, limit, statuses: activeStatuses, search, sortOrder: 'DESC' });
  }

  /* ---- Handlers ---- */

  function toggleStatus(s: string) {
    const next = { ...activeStatuses, [s]: !activeStatuses[s] };
    setActiveStatuses(next);
    setSkip(0);
    loadBookings({ skip: 0, limit, statuses: next, search, sortOrder: 'DESC' });
  }

  function handleSearchChange(value: string) {
    setSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSkip(0);
      loadBookings({ skip: 0, limit, statuses: activeStatuses, search: value, sortOrder: 'DESC' });
    }, 400);
  }

  function handlePageChange(next: { skip: number; limit: number }) {
    setSkip(next.skip);
    setLimit(next.limit);
    loadBookings({ skip: next.skip, limit: next.limit, statuses: activeStatuses, search, sortOrder: 'DESC' });
  }

  /* ---- Load lookups for edit ---- */

  const loadLookups = useCallback(async () => {
    if (lookupLoaded) return;
    try {
      const [svcRes, staffRes] = await Promise.all([
        bookingApi.listServices(),
        bookingApi.listAttendedBy(),
      ]);
      setAllServices(svcRes.data);
      setAllStaff(staffRes.data);
      setLookupLoaded(true);
    } catch {
      // silent — user can still edit other fields
    }
  }, [lookupLoaded]);

  /* ---- Open detail / edit ---- */

  function openDetail(b: BookingEntity) {
    setSelected(b);
    setEditing(false);
    setDraft(null);
    setError(null);
  }

  function startEdit() {
    if (!selected) return;
    setDraft(bookingToDraft(selected));
    setEditing(true);
    setError(null);
    loadLookups();
  }

  function cancelEdit() {
    setEditing(false);
    setDraft(null);
    setError(null);
  }

  function closeModal() {
    if (saving) return;
    setSelected(null);
    setEditing(false);
    setDraft(null);
    setError(null);
  }

  /* ---- Save edit ---- */

  async function handleSave() {
    if (!selected || !draft) return;
    setSaving(true);
    setError(null);
    try {
      const serviceMap = draft.serviceMap
        .filter((row) => row.serviceId)
        .map((row) => ({
          serviceId: row.serviceId,
          attendedBy: row.attendedBy.filter(Boolean),
          price: row.price ? Number(row.price) : undefined,
          serviceBookingItemId: row.serviceBookingItemId,
        }));

      const payload: Record<string, unknown> = {
        reservationDateTime: new Date(draft.reservationDateTime).toISOString(),
        paymentStatus: draft.paymentStatus,
        paymentMethod: draft.paymentMethod,
        status: draft.status,
        remark: draft.remark || undefined,
        attendedAddress: draft.attendedAddress || undefined,
        isExternal: draft.isExternal,
        serviceMap,
      };
      await bookingApi.updateBooking(selected.serviceBookingId, payload);
      setSelected(null);
      setEditing(false);
      setDraft(null);
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('booking.updateFailed'));
    } finally {
      setSaving(false);
    }
  }

  /* ---- Status quick actions (from detail view) ---- */

  async function changeStatus(booking: BookingEntity, newStatus: string) {
    setSaving(true);
    setError(null);
    try {
      await bookingApi.updateBookingStatus(booking.serviceBookingId, newStatus);
      setSelected(null);
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('booking.statusUpdateFailed'));
    } finally {
      setSaving(false);
    }
  }

  /* ---- Render ---- */

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="text-lg font-semibold text-slate-900 dark:text-slate-100">{t('booking.title')}</div>
          <div className="mt-1 text-sm text-slate-600 dark:text-slate-400">{t('booking.subtitle')}</div>
        </div>
        <div className="flex flex-wrap items-center gap-2 [&>button]:flex-1 sm:[&>button]:flex-none">
          <Button variant="secondary" onClick={refresh} disabled={loading}>
            {loading ? t('common.loading') : t('common.refresh')}
          </Button>
        </div>
      </div>

      {error && !selected ? (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-300">
          {error}
        </div>
      ) : null}

      {/* Table card */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900">
        {/* Filters */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div className="w-full sm:w-56">
            <TextField
              label={t('common.search')}
              placeholder={t('booking.searchPlaceholder')}
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400">
            {t('common.total')} <span className="font-semibold text-slate-700 dark:text-slate-300">{total}</span>
          </div>
        </div>

        {/* Status toggle buttons */}
        <div className="mt-3 flex flex-wrap gap-2">
          {BOOKING_STATUSES.map((s) => {
            const on = activeStatuses[s];
            const tone = statusTone(s);
            const colors = on
              ? {
                  amber: 'bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-700',
                  blue: 'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-700',
                  green: 'bg-green-100 text-green-800 border-green-300 dark:bg-green-950/40 dark:text-green-300 dark:border-green-700',
                  rose: 'bg-rose-100 text-rose-800 border-rose-300 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-700',
                  slate: 'bg-slate-100 text-slate-800 border-slate-300 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-600',
                }[tone]
              : 'bg-white text-slate-400 border-slate-200 dark:bg-slate-900 dark:text-slate-500 dark:border-slate-700';
            return (
              <button
                key={s}
                type="button"
                onClick={() => toggleStatus(s)}
                className={cn(
                  'rounded-full border px-3 py-1 text-xs font-medium capitalize transition-all',
                  colors,
                  on ? 'shadow-sm' : 'opacity-60 hover:opacity-80',
                )}
              >
                {t(`common.status.${toCamel(s)}`)}
              </button>
            );
          })}
        </div>

        {/* Table */}
        {/* Mobile card list */}
        <div className="mt-4 space-y-3 md:hidden">
          {loading && bookings.length === 0 ? (
            <DataCardEmpty>{t('booking.loadingBookings')}</DataCardEmpty>
          ) : bookings.length === 0 ? (
            <DataCardEmpty>{t('booking.emptyState')}</DataCardEmpty>
          ) : (
            bookings.map((b) => (
              <DataCard key={b.serviceBookingId} onClick={() => openDetail(b)}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="truncate font-medium text-slate-900 dark:text-slate-100">
                      {getServiceNames(b)}
                    </div>
                    <div className="truncate font-mono text-xs text-slate-500 dark:text-slate-400">
                      {b.bookingId ?? b.serviceBookingId.slice(0, 8)}
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1">
                    <Badge tone={statusTone(b.status)}>{t(`common.status.${toCamel(b.status)}`)}</Badge>
                    <Badge tone={paymentTone(b.paymentStatus)}>
                      {t(`common.status.${toCamel(b.paymentStatus)}`)}
                    </Badge>
                  </div>
                </div>
                <div className="mt-2 divide-y divide-slate-100 dark:divide-slate-800">
                  <DataCardRow label={t('booking.table.customer')}>{b.customer?.name ?? '—'}</DataCardRow>
                  <DataCardRow label={t('booking.table.reservation')}>
                    {formatDate(b.reservationDateTime)}
                  </DataCardRow>
                  <DataCardRow label={t('booking.table.duration')}>
                    {t('booking.minutesSuffix', { value: b.reservationDuration })}
                  </DataCardRow>
                  <DataCardRow label={t('booking.table.totalPrice')}>
                    {formatPrice(b.totalPrice)}
                  </DataCardRow>
                </div>
                <DataCardActions>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); openDetail(b); }}
                  >
                    {t('booking.actions.view')}
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelected(b);
                      setDraft(bookingToDraft(b));
                      setEditing(true);
                      setError(null);
                      loadLookups();
                    }}
                  >
                    {t('booking.actions.edit')}
                  </Button>
                </DataCardActions>
              </DataCard>
            ))
          )}
        </div>

        <div className="mt-4 hidden overflow-x-auto md:block">
          <table className="w-full text-left text-sm">
            <thead className="text-xs text-slate-500 dark:text-slate-400">
              <tr>
                <th className="py-2 pr-4 font-semibold">{t('booking.table.bookingId')}</th>
                <th className="py-2 pr-4 font-semibold">{t('booking.table.services')}</th>
                <th className="py-2 pr-4 font-semibold">{t('booking.table.customer')}</th>
                <th className="py-2 pr-4 font-semibold">{t('booking.table.reservation')}</th>
                <th className="py-2 pr-4 font-semibold text-right">{t('booking.table.duration')}</th>
                <th className="py-2 pr-4 font-semibold text-right">{t('booking.table.totalPrice')}</th>
                <th className="py-2 pr-4 font-semibold">{t('booking.table.status')}</th>
                <th className="py-2 pr-4 font-semibold">{t('booking.table.payment')}</th>
                <th className="py-2 pr-0 font-semibold text-right">{t('booking.table.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {loading && bookings.length === 0 ? (
                <tr>
                  <td className="py-8 text-center text-slate-500 dark:text-slate-400" colSpan={9}>
                    {t('booking.loadingBookings')}
                  </td>
                </tr>
              ) : bookings.length === 0 ? (
                <tr>
                  <td className="py-8 text-center text-slate-500 dark:text-slate-400" colSpan={9}>
                    {t('booking.emptyState')}
                  </td>
                </tr>
              ) : (
                bookings.map((b) => (
                  <tr
                    key={b.serviceBookingId}
                    className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800"
                    onClick={() => openDetail(b)}
                  >
                    <td className="py-3 pr-4 font-mono text-xs text-slate-500 dark:text-slate-400">{b.bookingId ?? b.serviceBookingId.slice(0, 8)}</td>
                    <td className="py-3 pr-4 font-medium text-slate-900 dark:text-slate-100 max-w-[200px] truncate">{getServiceNames(b)}</td>
                    <td className="py-3 pr-4 text-slate-700 dark:text-slate-300">{b.customer?.name ?? '—'}</td>
                    <td className="py-3 pr-4 text-slate-700 dark:text-slate-300 whitespace-nowrap">{formatDate(b.reservationDateTime)}</td>
                    <td className="py-3 pr-4 text-right text-slate-700 dark:text-slate-300">{t('booking.minutesSuffix', { value: b.reservationDuration })}</td>
                    <td className="py-3 pr-4 text-right text-slate-700 dark:text-slate-300 whitespace-nowrap">{formatPrice(b.totalPrice)}</td>
                    <td className="py-3 pr-4">
                      <Badge tone={statusTone(b.status)}>{t(`common.status.${toCamel(b.status)}`)}</Badge>
                    </td>
                    <td className="py-3 pr-4">
                      <Badge tone={paymentTone(b.paymentStatus)}>{t(`common.status.${toCamel(b.paymentStatus)}`)}</Badge>
                    </td>
                    <td className="py-3 pr-0">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); openDetail(b); }}
                        >
                          {t('booking.actions.view')}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelected(b);
                            setDraft(bookingToDraft(b));
                            setEditing(true);
                            setError(null);
                            loadLookups();
                          }}
                        >
                          {t('booking.actions.edit')}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="mt-4">
          <Pagination
            total={total}
            limit={limit}
            skip={skip}
            onChange={handlePageChange}
          />
        </div>
      </div>

      {/* Detail / Edit Modal */}
      <Modal
        open={!!selected}
        title={editing ? t('booking.detail.editTitle') : t('booking.detail.viewTitle')}
        onClose={closeModal}
        wide
      >
        {selected && !editing ? (
          /* ---- VIEW MODE ---- */
          <div className="space-y-4">
            {error ? (
              <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-300">{error}</div>
            ) : null}

            <div className="divide-y divide-slate-100 dark:divide-slate-800">
              <DetailRow label={t('booking.detail.bookingId')} value={selected.bookingId ?? selected.serviceBookingId.slice(0, 8)} />
              <DetailRow label={t('booking.detail.customer')} value={selected.customer?.name} />
              <DetailRow
                label={t('booking.detail.customerContact')}
                value={[selected.customer?.email, selected.customer?.phoneNo].filter(Boolean).join(' · ') || undefined}
              />
              <DetailRow label={t('booking.detail.attendedBy')} value={selected.attendedByUser?.name} />
              <DetailRow label={t('booking.detail.reservation')} value={formatDate(selected.reservationDateTime)} />
              <DetailRow
                label={t('booking.detail.endTime')}
                value={selected.reservationEndDateTime ? formatDate(selected.reservationEndDateTime) : undefined}
              />
              <DetailRow label={t('booking.detail.duration')} value={t('booking.minutesSuffix', { value: selected.reservationDuration })} />
              {selected.attendedAddress ? <DetailRow label={t('booking.detail.address')} value={selected.attendedAddress} /> : null}
              {selected.isExternal ? <DetailRow label={t('booking.detail.external')} value={t('booking.detail.yes')} /> : null}
              <DetailRow
                label={t('booking.detail.status')}
                value={<Badge tone={statusTone(selected.status)}>{t(`common.status.${toCamel(selected.status)}`)}</Badge>}
              />
              <DetailRow
                label={t('booking.detail.payment')}
                value={<Badge tone={paymentTone(selected.paymentStatus)}>{t(`common.status.${toCamel(selected.paymentStatus)}`)}</Badge>}
              />
              <DetailRow label={t('booking.detail.paymentMethod')} value={selected.paymentMethod ? t(`common.paymentMethods.${toCamel(selected.paymentMethod)}`) : undefined} />
              <DetailRow label={t('booking.detail.remark')} value={selected.remark} />
              <DetailRow label={t('booking.detail.created')} value={formatDate(selected.createdAt)} />
            </div>

            {/* Service items breakdown */}
            {selected.serviceJson && Array.isArray(selected.serviceJson) && selected.serviceJson.length > 0 ? (
              <div className="space-y-2">
                <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">{t('booking.detail.services')}</div>
                <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-slate-50 text-xs text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                      <tr>
                        <th className="px-3 py-2 font-semibold">{t('booking.detail.service')}</th>
                        <th className="px-3 py-2 font-semibold text-right">{t('booking.detail.price')}</th>
                        <th className="px-3 py-2 font-semibold text-right">{t('booking.detail.duration')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                      {selected.serviceJson.map((svc, idx) => (
                        <tr key={svc.serviceId || idx}>
                          <td className="px-3 py-2 text-slate-900 dark:text-slate-100">{svc.title}</td>
                          <td className="px-3 py-2 text-right text-slate-700 dark:text-slate-300">{formatPrice(svc.price)}</td>
                          <td className="px-3 py-2 text-right text-slate-700 dark:text-slate-300">{svc.unitValue != null ? t('booking.minutesSuffix', { value: svc.unitValue }) : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-slate-50 dark:bg-slate-800">
                      <tr>
                        <td className="px-3 py-2 text-sm font-semibold text-slate-900 dark:text-slate-100">{t('booking.detail.total')}</td>
                        <td className="px-3 py-2 text-right text-sm font-semibold text-slate-900 dark:text-slate-100">{formatPrice(selected.totalPrice)}</td>
                        <td className="px-3 py-2 text-right text-sm text-slate-500 dark:text-slate-400">{t('booking.minutesSuffix', { value: selected.reservationDuration })}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            ) : (
              <div className="divide-y divide-slate-100 dark:divide-slate-800">
                <DetailRow label={t('booking.detail.service')} value={selected.service?.title} />
                <DetailRow label={t('booking.detail.totalPrice')} value={formatPrice(selected.totalPrice)} />
              </div>
            )}

            {/* Action buttons */}
            <div className="flex flex-col gap-2 border-t border-slate-200 pt-4 sm:flex-row sm:justify-between dark:border-slate-700">
              <Button variant="secondary" size="sm" onClick={startEdit}>
                {t('booking.actions.edit')}
              </Button>
              <div className="flex gap-2 [&>button]:flex-1 sm:[&>button]:flex-none">
                {selected.status === 'pending' ? (
                  <>
                    <Button variant="danger" size="sm" disabled={saving} onClick={() => changeStatus(selected, 'cancelled')}>
                      {saving ? t('booking.actions.updating') : t('booking.actions.cancelBooking')}
                    </Button>
                    <Button size="sm" disabled={saving} onClick={() => changeStatus(selected, 'confirmed')}>
                      {saving ? t('booking.actions.updating') : t('booking.actions.confirm')}
                    </Button>
                  </>
                ) : null}
                {selected.status === 'confirmed' ? (
                  <>
                    <Button variant="danger" size="sm" disabled={saving} onClick={() => changeStatus(selected, 'cancelled')}>
                      {saving ? t('booking.actions.updating') : t('booking.actions.cancelBooking')}
                    </Button>
                    <Button size="sm" disabled={saving} onClick={() => changeStatus(selected, 'completed')}>
                      {saving ? t('booking.actions.updating') : t('booking.actions.complete')}
                    </Button>
                  </>
                ) : null}
              </div>
            </div>
          </div>
        ) : null}

        {selected && editing && draft ? (
          /* ---- EDIT MODE ---- */
          <div className="space-y-4">
            {error ? (
              <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-300">{error}</div>
            ) : null}

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <label className="block">
                <div className="mb-1.5 text-sm font-medium text-slate-800 dark:text-slate-200">{t('booking.edit.reservationDateTime')}</div>
                <input
                  type="datetime-local"
                  className={selectClass}
                  value={draft.reservationDateTime}
                  onChange={(e) => setDraft({ ...draft, reservationDateTime: e.target.value })}
                />
              </label>

              <div>
                <div className="mb-1.5 text-sm font-medium text-slate-800 dark:text-slate-200">{t('booking.edit.status')}</div>
                <div className="flex flex-wrap gap-2">
                  {BOOKING_STATUSES.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setDraft({ ...draft, status: s })}
                      className={cn(
                        'rounded-full border px-3 py-1.5 text-xs font-medium transition capitalize',
                        draft.status === s
                          ? 'border-red-300 bg-red-50 text-red-700 dark:border-red-700 dark:bg-red-950/40 dark:text-red-300'
                          : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400 dark:hover:border-slate-600',
                      )}
                    >
                      {t(`common.status.${toCamel(s)}`)}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <div className="mb-1.5 text-sm font-medium text-slate-800 dark:text-slate-200">{t('booking.edit.paymentStatus')}</div>
                <div className="flex flex-wrap gap-2">
                  {PAYMENT_STATUSES.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setDraft({ ...draft, paymentStatus: s })}
                      className={cn(
                        'rounded-full border px-3 py-1.5 text-xs font-medium transition capitalize',
                        draft.paymentStatus === s
                          ? 'border-red-300 bg-red-50 text-red-700 dark:border-red-700 dark:bg-red-950/40 dark:text-red-300'
                          : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400 dark:hover:border-slate-600',
                      )}
                    >
                      {t(`common.status.${toCamel(s)}`)}
                    </button>
                  ))}
                </div>
              </div>

              <label className="block">
                <div className="mb-1.5 text-sm font-medium text-slate-800 dark:text-slate-200">{t('booking.edit.paymentMethod')}</div>
                <select
                  className={selectClass}
                  value={draft.paymentMethod}
                  onChange={(e) => setDraft({ ...draft, paymentMethod: e.target.value })}
                >
                  {PAYMENT_METHODS.map((m) => (
                    <option key={m} value={m}>{t(`common.paymentMethods.${toCamel(m)}`)}</option>
                  ))}
                </select>
              </label>

              <label className="block sm:col-span-2">
                <div className="mb-1.5 text-sm font-medium text-slate-800 dark:text-slate-200">{t('booking.edit.attendedAddress')}</div>
                <input
                  type="text"
                  className={selectClass}
                  value={draft.attendedAddress}
                  onChange={(e) => setDraft({ ...draft, attendedAddress: e.target.value })}
                  placeholder={t('booking.edit.attendedAddressPlaceholder')}
                />
              </label>

              <label className="flex items-center gap-2 text-sm text-slate-800 dark:text-slate-200">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-300 text-red-600 focus:ring-red-500 dark:border-slate-600"
                  checked={draft.isExternal}
                  onChange={(e) => setDraft({ ...draft, isExternal: e.target.checked })}
                />
                {t('booking.edit.externalBooking')}
              </label>
            </div>

            <label className="block">
              <div className="mb-1.5 text-sm font-medium text-slate-800 dark:text-slate-200">{t('booking.edit.remark')}</div>
              <textarea
                className={cn(selectClass, 'min-h-[80px] resize-y')}
                value={draft.remark}
                onChange={(e) => setDraft({ ...draft, remark: e.target.value })}
                placeholder={t('booking.edit.remarkPlaceholder')}
                rows={3}
              />
            </label>

            {/* Editable service map */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">{t('booking.edit.servicesSection')}</div>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() =>
                    setDraft({
                      ...draft,
                      serviceMap: [...draft.serviceMap, { serviceId: '', attendedBy: [''], price: '' }],
                    })
                  }
                >
                  {t('booking.edit.addService')}
                </Button>
              </div>

              {draft.serviceMap.length === 0 ? (
                <div className="rounded-lg border border-dashed border-slate-300 p-4 text-center text-sm text-slate-500 dark:border-slate-600 dark:text-slate-400">
                  {t('booking.edit.noServicesAdded')}
                </div>
              ) : (
                <div className="space-y-3">
                  {draft.serviceMap.map((row, idx) => {
                    const matchedService = allServices.find((s) => s.serviceId === row.serviceId);
                    const isDeletedService = row.serviceId && !matchedService;
                    const deletedSnapshot = isDeletedService
                      ? selected?.serviceJson?.find((s) => s.serviceId === row.serviceId)
                      : null;
                    return (
                      <div
                        key={idx}
                        className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-3 dark:border-slate-700 dark:bg-slate-800/60"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="text-xs font-semibold text-slate-500 dark:text-slate-400">{t('booking.edit.serviceNumber', { n: idx + 1 })}</div>
                          {draft.serviceMap.length > 1 ? (
                            <button
                              type="button"
                              className="text-xs text-rose-500 hover:text-rose-700 dark:text-rose-400 dark:hover:text-rose-300"
                              onClick={() => {
                                const next = draft.serviceMap.filter((_, i) => i !== idx);
                                setDraft({ ...draft, serviceMap: next });
                              }}
                            >
                              {t('booking.edit.removeService')}
                            </button>
                          ) : null}
                        </div>

                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                          <label className="block">
                            <div className="mb-1 text-xs font-medium text-slate-700 dark:text-slate-300">{t('booking.edit.service')}</div>
                            {isDeletedService ? (
                              <div className={cn(selectClass, 'flex items-center gap-1.5 bg-slate-100 text-slate-500 cursor-not-allowed dark:bg-slate-800 dark:text-slate-500')}>
                                <span className="truncate">{deletedSnapshot?.title ?? row.serviceId.slice(0, 8)}</span>
                                <span className="shrink-0 rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-semibold text-rose-600 dark:bg-rose-950/40 dark:text-rose-300">{t('booking.edit.deleted')}</span>
                              </div>
                            ) : (
                              <select
                                className={selectClass}
                                value={row.serviceId}
                                onChange={(e) => {
                                  const next = [...draft.serviceMap];
                                  next[idx] = { ...next[idx], serviceId: e.target.value };
                                  const svc = allServices.find((s) => s.serviceId === e.target.value);
                                  if (svc && !next[idx].price) {
                                    next[idx].price = String(svc.price);
                                  }
                                  setDraft({ ...draft, serviceMap: next });
                                }}
                              >
                                <option value="">{t('booking.edit.selectService')}</option>
                                {allServices.map((s) => (
                                  <option key={s.serviceId} value={s.serviceId}>
                                    {s.title} ({s.code})
                                  </option>
                                ))}
                              </select>
                            )}
                          </label>

                          <CurrencyInput
                            label={t('booking.edit.price')}
                            value={row.price}
                            onChange={(val) => {
                              const next = [...draft.serviceMap];
                              next[idx] = { ...next[idx], price: val };
                              setDraft({ ...draft, serviceMap: next });
                            }}
                            placeholder={matchedService ? String(matchedService.price) : '0'}
                          />

                          <div>
                            <div className="mb-1 text-xs font-medium text-slate-700 dark:text-slate-300">
                              {t('booking.edit.attendedByField')}
                            </div>
                            <div className="space-y-1">
                              {row.attendedBy.map((staffId, sIdx) => (
                                <div key={sIdx} className="flex gap-1">
                                  <select
                                    className={cn(selectClass, 'flex-1')}
                                    value={staffId}
                                    onChange={(e) => {
                                      const next = [...draft.serviceMap];
                                      const ab = [...next[idx].attendedBy];
                                      ab[sIdx] = e.target.value;
                                      next[idx] = { ...next[idx], attendedBy: ab };
                                      setDraft({ ...draft, serviceMap: next });
                                    }}
                                  >
                                    <option value="">{t('booking.edit.selectStaff')}</option>
                                    {allStaff.map((u) => (
                                      <option key={u.userId} value={u.userId}>
                                        {u.name}
                                      </option>
                                    ))}
                                  </select>
                                  {row.attendedBy.length > 1 ? (
                                    <button
                                      type="button"
                                      className="px-1.5 text-rose-400 hover:text-rose-600 dark:text-rose-500 dark:hover:text-rose-300"
                                      onClick={() => {
                                        const next = [...draft.serviceMap];
                                        const ab = next[idx].attendedBy.filter((_, i) => i !== sIdx);
                                        next[idx] = { ...next[idx], attendedBy: ab };
                                        setDraft({ ...draft, serviceMap: next });
                                      }}
                                    >
                                      ✕
                                    </button>
                                  ) : null}
                                </div>
                              ))}
                              <button
                                type="button"
                                className="text-xs text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                                onClick={() => {
                                  const next = [...draft.serviceMap];
                                  next[idx] = { ...next[idx], attendedBy: [...next[idx].attendedBy, ''] };
                                  setDraft({ ...draft, serviceMap: next });
                                }}
                              >
                                {t('booking.edit.addStaff')}
                              </button>
                            </div>
                          </div>
                        </div>

                        {matchedService ? (
                          <div className="text-xs text-slate-500 dark:text-slate-400">
                            {t('booking.edit.durationCatalog', { duration: matchedService.unitValue ?? '—', price: formatPrice(matchedService.price) })}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Totals summary */}
              {draft.serviceMap.length > 0 ? (
                <div className="flex justify-end text-sm text-slate-700 dark:text-slate-300">
                  <span className="font-medium">
                    {t('booking.edit.estimatedTotal', {
                      amount: formatPrice(
                        draft.serviceMap.reduce((sum, r) => sum + (Number(r.price) || 0), 0)
                      ),
                    })}
                  </span>
                </div>
              ) : null}
            </div>

            {/* Customer (read-only context) */}
            <div className="rounded-lg border border-slate-100 bg-slate-50 p-3 text-sm text-slate-600 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-400">
              <span className="font-medium">{t('booking.edit.customerReadOnly')} </span>
              <span>{selected.customer?.name ?? '—'}</span>
            </div>

            <div className="flex justify-end gap-2 border-t border-slate-200 pt-4 [&>button]:flex-1 sm:[&>button]:flex-none dark:border-slate-700">
              <Button variant="secondary" size="sm" onClick={cancelEdit} disabled={saving}>
                {t('common.cancel')}
              </Button>
              <Button size="sm" onClick={handleSave} disabled={saving}>
                {saving ? t('booking.edit.saving') : t('booking.edit.save')}
              </Button>
            </div>
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
